import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { AbstractControl, FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatButton } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';

import { passwordMatchValidator } from '../../../core/utils';
import { RegisterCustomerData } from '../../../core/models';
import { AuthStore, RegistrationStore } from '../../../core/stores';
import { LanguageSwitcherComponent } from '../shared/language-switcher/language-switcher.component';
import { ThemeService } from '../../../core/services';
import * as Paths from '../../../core/constants';
import { FormErrorStateMatcher } from '../../../core/utils';
import { LanguageEnum } from '../../../core/enums';
import { passwordValidator } from '../../../core/utils/password-validator';

@Component({
  selector: 'dbx-registration',
  templateUrl: './registration.component.html',
  styleUrl: './registration.component.scss',
  host: {
    class: 'entry-page',
  },
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    MatIconModule,
    MatProgressSpinner,
    LanguageSwitcherComponent,
    MatButton,
    RouterLink,
    MatFormField,
    MatLabel,
    MatError,
    MatInputModule,
  ],
  providers: [RegistrationStore],
})
export class RegistrationComponent implements OnInit, OnDestroy {
  private readonly store = inject(RegistrationStore);
  private readonly authStore = inject(AuthStore);
  private readonly themeService = inject(ThemeService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);

  $language = this.authStore.language;
  submitted = signal<boolean>(false);

  hidePassword = true;
  hideConfirmPassword = true;
  qrCodeId: string = '';
  $manager = this.store.manager;
  $loading = this.store.loading;

  form = this.formBuilder.group(
    {
      companyName: ['', Validators.required, Validators.minLength(1)],
      firstName: ['', Validators.required, Validators.minLength(1)],
      lastName: ['', Validators.required, Validators.minLength(1)],
      email: ['', [Validators.required, Validators.email]],
      phone: [''],
      password: ['', [Validators.required, Validators.minLength(10), passwordValidator()]],
      confirm: ['', [Validators.required, Validators.minLength(10)]],
    },
    { validators: passwordMatchValidator('password', 'confirm') },
  );

  errorStateMatcher: FormErrorStateMatcher = new FormErrorStateMatcher(this.submitted);
  readonly Paths = Paths;

  togglePasswordVisibility(): void {
    this.hidePassword = !this.hidePassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.hideConfirmPassword = !this.hideConfirmPassword;
  }

  get companyName(): AbstractControl<string | null, string | null> | null {
    return this.form.get('companyName');
  }

  get firstName(): AbstractControl<string | null, string | null> | null {
    return this.form.get('firstName');
  }

  get lastName(): AbstractControl<string | null, string | null> | null {
    return this.form.get('lastName');
  }

  get email(): AbstractControl<string | null, string | null> | null {
    return this.form.get('email');
  }

  get phone(): AbstractControl<string | null, string | null> | null {
    return this.form.get('phone');
  }

  get password(): AbstractControl<string | null, string | null> | null {
    return this.form.get('password');
  }

  get confirm(): AbstractControl<string | null, string | null> | null {
    return this.form.get('confirm');
  }

  setLanguage(language: LanguageEnum) {
    this.authStore.setLanguage(language);
  }

  submit(): void {
    this.submitted.set(true);
    if (this.form.invalid || this.$loading()) {
      return;
    }
    const data: RegisterCustomerData = {
      qrCodeId: this.qrCodeId,
      companyName: this.companyName?.value ?? '',
      firstName: this.firstName?.value ?? '',
      lastName: this.lastName?.value ?? '',
      email: this.email?.value ?? '',
      phone: this.phone?.value ?? undefined,
      password: this.password?.value ?? '',
      language: this.authStore.language(),
    };
    this.store.register(data);
  }

  ngOnInit(): void {
    this.themeService.forceDarkTheme();
    this.route.queryParams.subscribe((params) => {
      this.qrCodeId = params['qrCodeId'] || params['boxId'];
      this.store.loadManager(this.qrCodeId);
    });
  }

  ngOnDestroy(): void {
    this.themeService.init();
  }
}
